'use client';

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  UploadIcon,
  XIcon,
  FileIcon,
} from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  subject: string;
  projectType: string;
  budgetRange: string;
  timeline: string;
  message: string;
  files: File[];
}

interface ContactFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSuccess, onError }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const projectTypes = [
    'Web Application',
    'AI/ML Solution',
    'Mobile App',
    'SaaS Platform',
    'E-commerce',
    'Custom Software',
    'Consultation',
    'Other',
  ];

  const budgetRanges = [
    'Under $5K',
    '$5K - $15K',
    '$15K - $30K',
    '$30K - $50K',
    '$50K+',
    "Let's Discuss",
  ];

  const timelines = [
    'ASAP',
    '1-2 weeks',
    '1 month',
    '2-3 months',
    '3+ months',
    'Flexible',
  ];

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    projectType: '',
    budgetRange: '',
    timeline: '',
    message: '',
    files: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const totalSteps = 4;

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<FormData> = {};

    switch (step) {
      case 1:
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Invalid email format';
        }
        break;
      case 2:
        if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
        if (!formData.projectType.trim())
          newErrors.projectType = 'Project type is required';
        break;
      case 3:
        if (!formData.budgetRange.trim())
          newErrors.budgetRange = 'Budget range is required';
        if (!formData.timeline.trim())
          newErrors.timeline = 'Timeline is required';
        if (!formData.message.trim()) newErrors.message = 'Message is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles: File[] = [];
    const newErrors: string[] = [];

    files.forEach(file => {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        newErrors.push(`${file.name} exceeds 5MB limit`);
        return;
      }

      // Check file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/png',
        'image/jpeg',
      ];

      if (!allowedTypes.includes(file.type)) {
        newErrors.push(`${file.name} is not an allowed file type`);
        return;
      }

      validFiles.push(file);
    });

    if (newErrors.length > 0) {
      onError?.(newErrors.join(', '));
      return;
    }

    setFormData(prev => ({ ...prev, files: [...prev.files, ...validFiles] }));
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles: File[] = [];
    const newErrors: string[] = [];

    files.forEach(file => {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        newErrors.push(`${file.name} exceeds 5MB limit`);
        return;
      }

      // Check file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/png',
        'image/jpeg',
      ];

      if (!allowedTypes.includes(file.type)) {
        newErrors.push(`${file.name} is not an allowed file type`);
        return;
      }

      validFiles.push(file);
    });

    if (newErrors.length > 0) {
      onError?.(newErrors.join(', '));
      return;
    }

    setFormData(prev => ({ ...prev, files: [...prev.files, ...validFiles] }));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      // Prepare form data for PHP handler
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('projectType', formData.projectType);
      formDataToSend.append('budgetRange', formData.budgetRange);
      formDataToSend.append('timeline', formData.timeline);
      formDataToSend.append('message', formData.message);

      // Add files to form data
      formData.files.forEach(file => {
        formDataToSend.append('files[]', file);
      });

      // Send to PHP handler
      const response = await fetch('/contact-handler.php', {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        onSuccess?.();
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          projectType: '',
          budgetRange: '',
          timeline: '',
          message: '',
          files: [],
        });
        setCurrentStep(1);
      } else {
        onError?.(result.error || 'Failed to send message');
      }
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepTitle = (step: number): string => {
    switch (step) {
      case 1:
        return 'Basic Info';
      case 2:
        return 'Project Details';
      case 3:
        return 'Message';
      case 4:
        return 'Files & Review';
      default:
        return '';
    }
  };

  const getStepDescription = (step: number): string => {
    switch (step) {
      case 1:
        return 'Tell us about yourself';
      case 2:
        return 'What project do you have in mind?';
      case 3:
        return 'Share your vision with us';
      case 4:
        return 'Add any relevant files and review';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Indicators */}
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                currentStep >= 1
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-border text-muted-foreground'
              }`}
            >
              <svg
                stroke="currentColor"
                fill="none"
                strokeWidth="2"
                viewBox="0 0 24 24"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <div
              className={`mx-2 h-0.5 w-16 transition-all duration-300 ${
                currentStep >= 2 ? 'bg-primary' : 'bg-border'
              }`}
            ></div>
          </div>
          <div className="flex items-center">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                currentStep >= 2
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-border text-muted-foreground'
              }`}
            >
              <svg
                stroke="currentColor"
                fill="none"
                strokeWidth="2"
                viewBox="0 0 24 24"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <div
              className={`mx-2 h-0.5 w-16 transition-all duration-300 ${
                currentStep >= 3 ? 'bg-primary' : 'bg-border'
              }`}
            ></div>
          </div>
          <div className="flex items-center">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                currentStep >= 3
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-border text-muted-foreground'
              }`}
            >
              <svg
                stroke="currentColor"
                fill="none"
                strokeWidth="2"
                viewBox="0 0 24 24"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <div
              className={`mx-2 h-0.5 w-16 transition-all duration-300 ${
                currentStep >= 4 ? 'bg-primary' : 'bg-border'
              }`}
            ></div>
          </div>
          <div className="flex items-center">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                currentStep >= 4
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-border text-muted-foreground'
              }`}
            >
              <svg
                stroke="currentColor"
                fill="none"
                strokeWidth="2"
                viewBox="0 0 24 24"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-2">
          <div className="flex justify-between text-sm">
            <span className="text-foreground font-medium">
              {getStepTitle(currentStep)}
            </span>
            <span className="text-muted-foreground">
              {currentStep} of {totalSteps}
            </span>
          </div>
          <p className="text-muted-foreground text-xs">
            {getStepDescription(currentStep)}
          </p>
        </div>
        <div className="bg-border h-2 w-full rounded-full">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          ></div>
        </div>
      </div>

      <form encType="multipart/form-data" className="space-y-6">
        <AnimatePresence mode="wait">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-card border-border rounded-2xl border p-6"
            >
              <div className="space-y-4">
                <div>
                  <label className="text-foreground mb-2 block text-sm font-medium">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    className={`border-border bg-background text-foreground placeholder-muted-foreground focus:ring-primary focus:border-primary w-full rounded-lg border p-3 transition-all focus:ring-2 ${
                      errors.name ? 'border-red-500' : ''
                    }`}
                    placeholder="Your name"
                    value={formData.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="text-foreground mb-2 block text-sm font-medium">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    className={`border-border bg-background text-foreground placeholder-muted-foreground focus:ring-primary focus:border-primary w-full rounded-lg border p-3 transition-all focus:ring-2 ${
                      errors.email ? 'border-red-500' : ''
                    }`}
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Project Details */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-card border-border rounded-2xl border p-6"
            >
              <div className="space-y-4">
                <div>
                  <label className="text-foreground mb-2 block text-sm font-medium">
                    Project Subject *
                  </label>
                  <input
                    type="text"
                    className={`border-border bg-background text-foreground placeholder-muted-foreground focus:ring-primary focus:border-primary w-full rounded-lg border p-3 transition-all focus:ring-2 ${
                      errors.subject ? 'border-red-500' : ''
                    }`}
                    placeholder="What's your project about?"
                    value={formData.subject}
                    onChange={e => handleInputChange('subject', e.target.value)}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-foreground mb-2 block text-sm font-medium">
                    Project Type *
                  </label>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {projectTypes.map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleInputChange('projectType', type)}
                        aria-label={`Select ${type} as project type`}
                        aria-pressed={formData.projectType === type}
                        className={`rounded-lg border-2 p-3 text-sm font-medium transition-all ${
                          formData.projectType === type
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-primary/5'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                  {errors.projectType && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.projectType}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Message */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-card border-border rounded-2xl border p-6"
            >
              <div className="space-y-6">
                <div>
                  <label className="text-foreground mb-2 block text-sm font-medium">
                    Budget Range *
                  </label>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {budgetRanges.map(range => (
                      <button
                        key={range}
                        type="button"
                        onClick={() => handleInputChange('budgetRange', range)}
                        aria-label={`Select ${range} as budget range`}
                        aria-pressed={formData.budgetRange === range}
                        className={`rounded-lg border-2 p-3 text-sm font-medium transition-all ${
                          formData.budgetRange === range
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-primary/5'
                        }`}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                  {errors.budgetRange && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.budgetRange}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-foreground mb-2 block text-sm font-medium">
                    Timeline *
                  </label>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {timelines.map(timeline => (
                      <button
                        key={timeline}
                        type="button"
                        onClick={() => handleInputChange('timeline', timeline)}
                        aria-label={`Select ${timeline} as timeline`}
                        aria-pressed={formData.timeline === timeline}
                        className={`rounded-lg border-2 p-3 text-sm font-medium transition-all ${
                          formData.timeline === timeline
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-primary/5'
                        }`}
                      >
                        {timeline}
                      </button>
                    ))}
                  </div>
                  {errors.timeline && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.timeline}
                    </p>
                  )}
                </div>

                <div className="flex w-full flex-col gap-2">
                  <label className="text-foreground mb-2 block text-sm font-medium">
                    Message *
                  </label>
                  <textarea
                    rows={6}
                    className={`bg-black contact-form-textarea border-primary text-foreground placeholder-muted-foreground/50 focus:ring-primary focus:border-primary block w-full resize-none rounded-lg border p-3 transition-all focus:ring-2 ${
                      errors.message ? 'border-red-500' : ''
                    }`}
                    style={{ backgroundColor: 'black !important' }}
                    placeholder="Tell us about your project, requirements, timeline, and any other details..."
                    value={formData.message}
                    onChange={e => handleInputChange('message', e.target.value)}
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.message}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Files & Review */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-card border-border rounded-2xl border p-6"
            >
              <div className="space-y-6">
                {/* File Upload */}
                <div>
                  <label className="text-foreground mb-2 block text-sm font-medium">
                    Attach Files (Optional)
                  </label>
                  <div
                    className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                      isDragOver
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="Upload project files"
                      aria-describedby="file-upload-description"
                      className="text-primary hover:text-primary/80 transition-colors"
                    >
                      <UploadIcon className="mx-auto mb-2 h-8 w-8" />
                      <p className="text-muted-foreground text-sm">
                        {isDragOver
                          ? 'Drop files here'
                          : 'Click to upload files or drag and drop'}
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        PDF, Word, Text, PNG, JPEG up to 5MB each
                      </p>
                    </button>
                  </div>
                </div>

                {/* File List */}
                {formData.files.length > 0 && (
                  <div>
                    <h4 className="text-foreground mb-3 text-sm font-medium">
                      Selected Files:
                    </h4>
                    <div className="space-y-2">
                      {formData.files.map((file, index) => (
                        <div
                          key={index}
                          className="bg-muted flex items-center justify-between rounded-lg p-3"
                        >
                          <div className="flex items-center space-x-2">
                            <FileIcon className="text-muted-foreground h-4 w-4" />
                            <span className="text-foreground text-sm">
                              {file.name}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            aria-label={`Remove file ${file.name}`}
                            className="text-red-500 transition-colors hover:text-red-700"
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Review Section */}
                <div className="border-t pt-4">
                  <h4 className="text-foreground mb-3 text-sm font-medium">
                    Review Your Information:
                  </h4>
                  <div className="bg-muted space-y-2 rounded-lg p-4 text-sm">
                    <p>
                      <strong>Name:</strong> {formData.name}
                    </p>
                    <p>
                      <strong>Email:</strong> {formData.email}
                    </p>
                    <p>
                      <strong>Subject:</strong> {formData.subject}
                    </p>
                    <p>
                      <strong>Project Type:</strong> {formData.projectType}
                    </p>
                    <p>
                      <strong>Budget Range:</strong> {formData.budgetRange}
                    </p>
                    <p>
                      <strong>Timeline:</strong> {formData.timeline}
                    </p>
                    <p>
                      <strong>Message:</strong>{' '}
                      {formData.message.substring(0, 100)}
                      {formData.message.length > 100 ? '...' : ''}
                    </p>
                    <p>
                      <strong>Files:</strong> {formData.files.length} attached
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            type="button"
            disabled={currentStep === 1}
            onClick={prevStep}
            aria-label="Go to previous step"
            aria-describedby={
              currentStep === 1 ? 'first-step-disabled' : undefined
            }
            className="border-border text-muted-foreground hover:text-foreground hover:border-foreground cursor-pointer rounded-lg border px-6 py-3 transition-all disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowLeftIcon className="mr-2 inline h-4 w-4" />
            Previous
          </button>

          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={nextStep}
              aria-label="Go to next step"
              className="bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer rounded-lg px-6 py-3 transition-all"
            >
              Next
              <ArrowRightIcon className="ml-2 inline h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              aria-label={isSubmitting ? 'Sending message...' : 'Send message'}
              aria-describedby={isSubmitting ? 'submitting-status' : undefined}
              className="bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer rounded-lg px-6 py-3 transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
